import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const DATED = ["EXTENSION", "AMENDMENT"];

export function describeChangePayload(p) {
  if (!p) return null;
  const bits = [];
  if (p.check_in_date) bits.push(`check-in ${p.check_in_date}`);
  if (p.check_out_date) bits.push(`check-out ${p.check_out_date}`);
  if (p.room_count) bits.push(`${p.room_count} room(s)`);
  return bits.length ? bits.join(" · ") : null;
}

export function ChangeDecisionDialog({ change, stays, open, onOpenChange, onSubmit, busy }) {
  const dated = change && DATED.includes(change.change_type);
  const activeStays = (stays || []).filter((s) => s.status !== "CANCELLED");
  const [form, setForm] = useState({ stay_id: "", check_in_date: "", check_out_date: "", room_count: "", notes: "" });

  useEffect(() => {
    if (!change) return;
    const req = change.requested_payload || {};
    const stay = activeStays.find((s) => s.id === req.stay_id) || (activeStays.length === 1 ? activeStays[0] : null);
    setForm({
      stay_id: stay?.id || "",
      check_in_date: req.check_in_date || stay?.check_in_date || "",
      check_out_date: req.check_out_date || stay?.check_out_date || "",
      room_count: req.room_count ? String(req.room_count) : stay ? String(stay.room_count) : "",
      notes: "",
    });
  }, [change]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickStay = (id) => {
    const s = activeStays.find((x) => x.id === id);
    setForm((f) => ({ ...f, stay_id: id, check_in_date: s?.check_in_date || f.check_in_date, check_out_date: s?.check_out_date || f.check_out_date, room_count: s ? String(s.room_count) : f.room_count }));
  };

  const approve = () => {
    const applied = dated ? {
      stay_id: form.stay_id || undefined, check_in_date: form.check_in_date || undefined,
      check_out_date: form.check_out_date || undefined, room_count: form.room_count ? Number(form.room_count) : undefined,
    } : undefined;
    onSubmit("APPROVED", form.notes || null, applied);
  };

  if (!change) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-decision-dialog">
        <DialogHeader>
          <DialogTitle>Decide {change.change_type.toLowerCase()} request</DialogTitle>
          <DialogDescription>
            Client request (kept unchanged): &ldquo;{change.reason}&rdquo;
            {describeChangePayload(change.requested_payload) && <> &middot; requested {describeChangePayload(change.requested_payload)}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {dated && (
            <>
              {activeStays.length > 1 && (
                <div>
                  <Label>Stay</Label>
                  <Select value={form.stay_id} onValueChange={pickStay}>
                    <SelectTrigger data-testid="decision-stay-select"><SelectValue placeholder="Choose the stay" /></SelectTrigger>
                    <SelectContent>{activeStays.map((s) => <SelectItem key={s.id} value={s.id}>{s.property_name} · {s.check_in_date} → {s.check_out_date}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>New check-in</Label><Input data-testid="decision-check-in-input" type="date" value={form.check_in_date} onChange={(e) => setForm({ ...form, check_in_date: e.target.value })} /></div>
                <div><Label>New check-out</Label><Input data-testid="decision-check-out-input" type="date" value={form.check_out_date} onChange={(e) => setForm({ ...form, check_out_date: e.target.value })} /></div>
              </div>
              <div><Label>Rooms</Label><Input data-testid="decision-rooms-input" type="number" min="1" step="1" value={form.room_count} onChange={(e) => setForm({ ...form, room_count: e.target.value })} /></div>
              <p className="text-xs text-jigsaw-muted">Availability and mandatory compliance for the new dates are re-checked server-side before anything is applied.</p>
            </>
          )}
          <div><Label>Decision notes (optional)</Label><Textarea data-testid="decision-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter className="gap-2">
          <Button data-testid="decision-reject-button" variant="outline" className="text-jigsaw-danger" disabled={busy} onClick={() => onSubmit("REJECTED", form.notes || null)}>Reject</Button>
          <Button data-testid="decision-approve-button" className="bg-jigsaw-success text-white hover:bg-jigsaw-success/90" disabled={busy} onClick={approve}>Approve &amp; apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
